import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type {
  OperationalExpense,
  PaginatedOperationalExpenses,
} from '@/types/operational-expense'
import type { OperationalExpenseFormData } from '@/validators/operational-expense'

export interface ListOperationalExpenseParams {
  search?: string
  categoryId?: string
  accountId?: string
  vehicleId?: string
  freightId?: string
  method?: 'PIX' | 'BANK_TRANSFER' | 'BANK_SLIP' | 'CASH' | 'OTHER'
  startDate?: string
  endDate?: string
  page?: number
  perPage?: number
}

const KEYS = {
  list: (params: ListOperationalExpenseParams) => ['operational-expenses', params] as const,
  detail: (id: string) => ['operational-expenses', id] as const,
}

export function useOperationalExpenses(params: ListOperationalExpenseParams = {}) {
  return useQuery({
    queryKey: KEYS.list(params),
    queryFn: () =>
      api
        .get<PaginatedOperationalExpenses>('/operational-expenses', { params })
        .then((r) => r.data),
  })
}

export function useOperationalExpense(id: string) {
  return useQuery({
    queryKey: KEYS.detail(id),
    queryFn: () =>
      api.get<OperationalExpense>(`/operational-expenses/${id}`).then((r) => r.data),
    enabled: !!id,
  })
}

export function useCreateOperationalExpense() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: OperationalExpenseFormData) =>
      api.post<OperationalExpense>('/operational-expenses', data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['operational-expenses'] })
      // Despesa gera lançamento no fluxo de caixa
      qc.invalidateQueries({ queryKey: ['cash-transactions'] })
      qc.invalidateQueries({ queryKey: ['cash-accounts'] })
      qc.invalidateQueries({ queryKey: ['cash-flow-summary'] })
    },
  })
}
